import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('dnrPreferences');
export interface FullDNRDetails {
  specificInterventionsToDecline?: string;
  discussedWithHealthcareProvider?: boolean;
  conditionsForCPR?: string;
  additionalInstructions?: string;
}

export interface LimitedResuscitationDetails {
  acceptableInterventions?: string;
  limitedResuscitationConditions?: string;
  medicationPreferences?: string;
  timeLimits?: string;
}

export interface SpecificMedicalSituationDetails {
  triggeringConditions?: string;
  exceptions?: string;
  treatmentPreferences?: string;
  discussedWithDoctor?: boolean;
}

export interface NaturalDeathDetails {
  comfortCarePreferences?: string;
  painManagementOptions?: string;
  spiritualCulturalPractices?: string;
  environmentPreferences?: string;
}

export type DNRDetails =
  | FullDNRDetails
  | LimitedResuscitationDetails
  | SpecificMedicalSituationDetails
  | NaturalDeathDetails
  | null;

export interface DNRPreferencesInfoRec {
  id: string;
  userId: string;
  dnrType?: string;
  locationOfDirective?: string;
  specialInstructions?: string;
  dnrDetails?: DNRDetails;
  notes?: string;
  isLocked?: boolean;
}

class DNRPreferencesInfoService {
  async getAllDNRPreferences(): Promise<DNRPreferencesInfoRec[]> {
    const response = await http.get('/api/dnr-preferences');

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch DNR preferences information');
    }

    return response.data.data || [];
  }

  async getDNRPreferencesById(id: string): Promise<DNRPreferencesInfoRec | null> {
    try {
      const response = await http.get(`/api/dnr-preferences/${id}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch DNR preferences information');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching DNR preferences information by ID:', error);
      return null;
    }
  }

  async createDNRPreferences(dnrData: Partial<DNRPreferencesInfoRec>): Promise<DNRPreferencesInfoRec> {
    const response = await http.post('/api/dnr-preferences', dnrData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create DNR preferences information');
    }

    return response.data.data;
  }

  async updateDNRPreferences(id: string, dnrData: Partial<DNRPreferencesInfoRec>): Promise<DNRPreferencesInfoRec> {
    const response = await http.patch(`/api/dnr-preferences/${id}`, dnrData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update DNR preferences information');
    }

    return response.data.data;
  }

  async deleteDNRPreferences(id: string): Promise<void> {
    const response = await http.delete(`/api/dnr-preferences/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete DNR preferences information');
    }
  }

  async lockDNRPreferences(id: string): Promise<void> {
    const response = await http.post(`/api/dnr-preferences/${id}/lock`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock DNR preferences information');
    }
  }

  async unlockDNRPreferences(id: string, granteeEmails: string[]): Promise<void> {
    logger.debug('[Service] Unlocking DNR preferences with ID:', id, 'for emails:', granteeEmails);
    const response = await http.post(`/api/dnr-preferences/${id}/unlock`, { granteeEmails });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock DNR preferences information');
    }
    logger.debug('[Service] Unlock completed');
  }

  async getDNRPreferencesAccessGrants(id: string): Promise<any[]> {
    try {
      logger.debug('[Service] Getting access grants for DNR preferences with ID:', id);
      const response = await http.get(`/api/dnr-preferences/${id}/grants`);
      logger.debug('[Service] Access grants response:', response.data);

      return response.data.data || [];
    } catch (error) {
      logger.error('[Service] Error getting DNR preferences access grants:', error);
      throw error;
    }
  }
}

export const dnrPreferencesService = new DNRPreferencesInfoService();